/**
 * HTTP status message helpers for humanizeError
 * Extracted to reduce function size
 */

import type { UserFriendlyError } from "../types";
import { HTTP_STATUS_MESSAGES } from "../constants";
import { getLoginHttp401UnauthorizedUserFriendlyError } from "./loginSiteTextUserFriendlyErrors";
import { getContextualAccessMessage } from "./errorContextHelpers";
import { organizationNotFoundMessage, organizationNotFoundTitle } from "@Front/Utils/publicSiteBranding";

const ELEMENT_404_NAMES: Record<string, { title: string; message: string }> = {
	folder: { title: "Dossier introuvable", message: "Le dossier demandé n'existe pas, a été supprimé ou vous n'y avez pas accès." },
	document: { title: "Document introuvable", message: "Le document demandé n'existe pas, a été supprimé ou vous n'y avez pas accès." },
	file: { title: "Fichier introuvable", message: "Le fichier demandé n'existe pas, a été supprimé ou vous n'y avez pas accès." },
	customer: { title: "Client introuvable", message: "Le client demandé n'existe pas ou n'est plus actif." },
	user: { title: "Utilisateur introuvable", message: "L'utilisateur demandé n'existe pas ou n'est plus actif." },
	office: { title: organizationNotFoundTitle(), message: organizationNotFoundMessage() },
	share: { title: "Partage introuvable", message: "Le partage de dossier demandé n'existe pas ou a été révoqué." },
	plan: { title: "Plan introuvable", message: "Le plan d'abonnement demandé n'existe pas ou n'est plus disponible." },
	subscription: { title: "Abonnement introuvable", message: "L'abonnement demandé n'existe pas ou n'est plus actif." },
	deed: { title: "Acte introuvable", message: "L'acte demandé n'existe pas ou a été supprimé." },
	anchor: { title: "Ancrage introuvable", message: "Aucun ancrage blockchain trouvé pour cet élément." },
	reminder: { title: "Rappel introuvable", message: "La configuration de rappel demandée n'existe pas." },
	notification: { title: "Notification introuvable", message: "La notification demandée n'existe pas ou a été supprimée." },
	role: { title: "Rôle introuvable", message: "Le rôle demandé n'existe pas ou a été supprimé." },
	rib: { title: "RIB introuvable", message: "Le fichier RIB demandé n'existe pas ou n'est pas configuré." },
	text: { title: "Texte introuvable", message: "Le texte demandé n'existe pas ou n'est pas configuré." },
	appointment: { title: "Rendez-vous introuvable", message: "Le rendez-vous demandé n'existe pas ou a été supprimé." },
	vote: { title: "Vote introuvable", message: "Le vote demandé n'existe pas ou a été supprimé." },
	"third party": { title: "Tiers introuvable", message: "Le tiers demandé n'existe pas ou n'est plus associé à ce dossier." },
	tiers: { title: "Tiers introuvable", message: "Le tiers demandé n'existe pas ou n'est plus associé à ce dossier." },
};

const ELEMENT_404_REGEX =
	/(folder|document|file|customer|user|office|share|plan|subscription|deed|anchor|reminder|notification|role|rib|text|appointment|vote|third party|tiers)/i;

type ErrorPattern = { pattern: RegExp; message: UserFriendlyError | ((match: RegExpMatchArray) => UserFriendlyError) };

function tryPatternMatch(errorMessage: string, patterns: ErrorPattern[]): UserFriendlyError | null {
	for (const { pattern, message } of patterns) {
		const match = errorMessage.match(pattern);
		if (match) return typeof message === "function" ? message(match) : message;
	}
	return null;
}

function try403BackendMessage(errorMessage: string): UserFriendlyError | null {
	if (!errorMessage.trim().includes("Votre statut")) return null;
	return { title: "Action interdite", message: errorMessage.trim(), canRetry: false };
}

function try401403Contextual(errorMessage: string, httpStatus: number): UserFriendlyError | null {
	if (httpStatus !== 401 && httpStatus !== 403) return null;
	return getContextualAccessMessage(errorMessage, httpStatus);
}

function try404Element(errorMessage: string): UserFriendlyError | null {
	const match = errorMessage.match(ELEMENT_404_REGEX);
	const elementType = match?.[1]?.toLowerCase();
	if (!elementType) return null;
	const elementInfo = ELEMENT_404_NAMES[elementType];
	if (!elementInfo) return null;
	return { title: elementInfo.title, message: elementInfo.message, canRetry: false };
}

function resolveHttpStatusUserFriendly(httpStatus: number, fallback: UserFriendlyError): UserFriendlyError {
	if (httpStatus === 401) {
		return getLoginHttp401UnauthorizedUserFriendlyError();
	}
	return fallback;
}

/**
 * Handles HTTP status-based error messages
 */
export function handleHttpStatusError(
	errorMessage: string | null | undefined,
	httpStatus: number,
	ERROR_PATTERNS: ErrorPattern[],
): UserFriendlyError | null {
	const statusMessage = HTTP_STATUS_MESSAGES[httpStatus];
	if (!statusMessage) return null;
	if (!errorMessage) return resolveHttpStatusUserFriendly(httpStatus, statusMessage);

	const patternResult = tryPatternMatch(errorMessage, ERROR_PATTERNS);
	if (patternResult) return patternResult;
	if (httpStatus === 403) {
		const backend403 = try403BackendMessage(errorMessage);
		if (backend403) return backend403;
	}
	const contextual = try401403Contextual(errorMessage, httpStatus);
	if (contextual) return contextual;
	if (httpStatus === 404) {
		const elementMsg = try404Element(errorMessage);
		if (elementMsg) return elementMsg;
	}
	return resolveHttpStatusUserFriendly(httpStatus, statusMessage);
}

/**
 * Extracts element-specific message for 404 errors
 */
export function extract404ElementMessage(errorMessage: string): UserFriendlyError | null {
	return try404Element(errorMessage);
}
